import React, { useState, useEffect } from 'react';
import { Eye, Clock, Copy, X, CheckCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

// Demo Banner Component - Shows at top when in demo mode
const DemoBanner = () => {
  const [isDemo, setIsDemo] = useState(false);
  const [demoInfo, setDemoInfo] = useState(null);
  const [timeLeft, setTimeLeft] = useState('');
  const [copied, setCopied] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    // Check if we're in demo mode - trust sessionStorage if it has a valid session
    const checkDemoStatus = () => {
      const activeDemo = sessionStorage.getItem('activeDemo');
      
      if (activeDemo) {
        try {
          const demoData = JSON.parse(activeDemo);
          // Check if session is still valid
          const expiresAt = demoData.expiresAt ? new Date(demoData.expiresAt) : null;
          if (expiresAt && expiresAt > new Date()) {
            setDemoInfo(demoData);
            setIsDemo(true);
            return;
          }
        } catch (e) {
          console.error('Failed to parse demo info:', e);
        }
      }
      setIsDemo(false);
    };
    
    checkDemoStatus();
    
    // Also listen for route changes (React Router uses pushState)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(this, args);
      checkDemoStatus();
    };
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(this, args);
      checkDemoStatus();
    };
    
    window.addEventListener('popstate', checkDemoStatus);
    
    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', checkDemoStatus);
    };
  }, []);
  
  // Update time left
  useEffect(() => {
    if (!demoInfo?.expiresAt) return;
    
    const updateTimeLeft = () => {
      const now = new Date();
      const expires = new Date(demoInfo.expiresAt);
      const diff = expires - now;
      
      if (diff <= 0) {
        setTimeLeft('Expired');
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setTimeLeft(`${hours}h ${minutes}m`);
    };
    
    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 60000);
    return () => clearInterval(interval);
  }, [demoInfo]);
  
  const handleCopyLink = () => {
    if (!demoInfo?.template?.id) return;
    const demoUrl = `${window.location.origin}/demo/${demoInfo.template.id}`;
    navigator.clipboard.writeText(demoUrl);
    setCopied(true);
    toast.success('Demo link copied!');
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleExitDemo = () => {
    // Clear demo data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    sessionStorage.removeItem('activeDemo');
    localStorage.removeItem(`demo_session_${demoInfo?.template?.id}`);
    
    // Redirect to home
    window.location.href = '/';
  };
  
  if (!isDemo || dismissed) return null;
  
  const templateColor = demoInfo?.template?.color || '#6B7280';
  
  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[9999] px-4 py-2 flex items-center justify-between gap-4"
      style={{ 
        backgroundColor: templateColor,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
      }}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 bg-black/20 px-3 py-1 rounded-full">
          <Eye size={14} className="text-white" />
          <span className="text-white text-xs font-bold uppercase tracking-wider">Demo Mode</span>
        </div>
        <span className="text-white/90 text-sm font-medium hidden sm:block">
          {demoInfo?.template?.name} • View Only
        </span>
      </div>
      
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 text-white/80 text-xs hidden md:flex">
          <Clock size={12} />
          <span>Expires in {timeLeft}</span>
        </div>
        
        <button
          onClick={handleCopyLink}
          className="flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-xs font-medium rounded-full transition-colors"
        >
          {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
          <span className="hidden sm:inline">{copied ? 'Copied!' : 'Share Demo'}</span>
        </button>
        
        <a
          href="/auth"
          className="flex items-center gap-1.5 px-3 py-1 bg-white text-gray-900 text-xs font-medium rounded-full hover:bg-white/90 transition-colors"
        >
          <span>Sign Up Free</span>
          <ExternalLink size={10} />
        </a>
        
        <button
          onClick={handleExitDemo}
          className="flex items-center gap-1.5 px-3 py-1 bg-black/30 hover:bg-black/40 text-white text-xs font-medium rounded-full transition-colors"
        >
          <X size={12} />
          <span className="hidden sm:inline">Exit</span>
        </button>
      </div>
    </div>
  );
};

export default DemoBanner;
